import React, { useId, useState } from "react";
import { FiAlertCircle, FiChevronDown, FiEye, FiEyeOff } from "react-icons/fi";
import DotLoader from "../../components/Loader/DotLoader";

// Building blocks for the dark sign-in / sign-up modal.

export const AuthHeader = ({ title, subtitle }: { title: string; subtitle?: React.ReactNode }) => (
  <div className="mb-6">
    <h2 className="text-2xl font-bold tracking-tight text-white">{title}</h2>
    {subtitle && <p className="mt-1.5 text-sm leading-6 text-slate-400">{subtitle}</p>}
  </div>
);

type FieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "className"> & {
  label: string;
  icon?: React.ReactNode;
  error?: string;
  /** Rendered at the right of the label row, e.g. a "Forgot password?" link. */
  labelAction?: React.ReactNode;
  trailing?: React.ReactNode;
};

export const AuthField = ({ label, icon, error, labelAction, trailing, id, ...input }: FieldProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-center justify-between">
        <label htmlFor={inputId} className="text-sm font-medium text-slate-300">
          {label}
        </label>
        {labelAction}
      </div>
      <div
        className={`flex h-12 items-center gap-3 rounded-xl border bg-white/[0.04] px-4 transition focus-within:bg-white/[0.06] ${
          error
            ? "border-red-400/60"
            : "border-white/10 focus-within:border-blue-400/70"
        }`}
      >
        {icon && <span className="shrink-0 text-slate-500">{icon}</span>}
        <input
          id={inputId}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          className="h-full min-w-0 flex-1 bg-transparent text-[15px] text-white outline-none placeholder:text-slate-600"
          {...input}
        />
        {trailing}
      </div>
      {error && (
        <p id={errorId} className="mt-1.5 text-xs text-red-300">
          {error}
        </p>
      )}
    </div>
  );
};

export const PasswordField = (props: Omit<FieldProps, "type" | "trailing">) => {
  const [visible, setVisible] = useState(false);
  return (
    <AuthField
      {...props}
      type={visible ? "text" : "password"}
      trailing={
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          className="shrink-0 rounded-md p-1 text-slate-500 transition hover:text-slate-200"
        >
          {visible ? <FiEyeOff size={18} /> : <FiEye size={18} />}
        </button>
      }
    />
  );
};

type SelectProps = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "className"> & {
  label: string;
  icon?: React.ReactNode;
  placeholder: string;
  options: { value: string; label: string }[];
};

export const AuthSelect = ({ label, icon, placeholder, options, id, value, ...select }: SelectProps) => {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  return (
    <div className="mb-4 min-w-0">
      <label htmlFor={selectId} className="mb-1.5 block text-sm font-medium text-slate-300">
        {label}
      </label>
      <div className="relative flex h-12 items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 transition focus-within:border-blue-400/70 focus-within:bg-white/[0.06]">
        {icon && <span className="pointer-events-none shrink-0 text-slate-500">{icon}</span>}
        <select
          id={selectId}
          value={value}
          {...select}
          className={`h-full min-w-0 flex-1 cursor-pointer appearance-none bg-transparent pr-6 text-[15px] outline-none ${
            value ? "text-white" : "text-slate-600"
          }`}
        >
          <option value="" disabled className="bg-[#0b1020] text-slate-400">
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value} className="bg-[#0b1020] text-white">
              {option.label}
            </option>
          ))}
        </select>
        <FiChevronDown aria-hidden className="pointer-events-none absolute right-4 text-slate-500" />
      </div>
    </div>
  );
};

export const AuthButton = ({
  loading,
  children,
  ...button
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) => (
  <button
    type="submit"
    disabled={loading || button.disabled}
    {...button}
    className="mt-2 flex h-12 w-full items-center justify-center rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-70"
  >
    {loading ? <DotLoader /> : children}
  </button>
);

export const AuthError = ({ message }: { message?: string }) =>
  message ? (
    <div
      role="alert"
      className="mb-4 flex items-start gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-200"
    >
      <FiAlertCircle className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  ) : null;

export const AuthLink = ({
  children,
  ...button
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    type="button"
    {...button}
    className="font-semibold text-blue-300 transition hover:text-blue-200 hover:underline"
  >
    {children}
  </button>
);

export const AuthSwitch = ({ children }: { children: React.ReactNode }) => (
  <p className="mt-6 border-t border-white/10 pt-5 text-center text-sm text-slate-400">{children}</p>
);
